import {
  DEFAULT_BINGO_SETTINGS,
  BINGO_GRID_SIZES,
  listBingoCategories,
  settingsFromPreset,
  type BingoCatalogValidation,
  type BingoCategoryId,
  type BingoGridSize,
  type BingoPresetId,
  type BingoSettings,
  type RoomSettings,
} from "@hitster/shared";

interface Props {
  settings: RoomSettings;
  onChange: (patch: Partial<RoomSettings>) => void;
  validation?: BingoCatalogValidation;
}

const TIMER_OPTIONS: Array<{ label: string; value: number | null }> = [
  { label: "15 s", value: 15 },
  { label: "20 s", value: 20 },
  { label: "25 s", value: 25 },
  { label: "30 s", value: 30 },
  { label: "Illimité", value: null },
];

export function BingoSettingsForm({ settings, onChange, validation }: Props) {
  const bingo: BingoSettings = {
    ...DEFAULT_BINGO_SETTINGS,
    ...settings.bingo,
    winConditions: {
      ...DEFAULT_BINGO_SETTINGS.winConditions,
      ...settings.bingo?.winConditions,
    },
  };
  const categories = listBingoCategories();

  const patchBingo = (partial: Partial<BingoSettings>) => {
    const next: BingoSettings = {
      ...bingo,
      ...partial,
      winConditions: { ...bingo.winConditions, ...partial.winConditions },
    };
    onChange({ gameKind: "bingo", bingo: next });
  };

  const applyPreset = (preset: BingoPresetId) => {
    patchBingo(settingsFromPreset(preset, { ...bingo, preset }));
  };

  const toggleCategory = (id: BingoCategoryId) => {
    const has = bingo.categories.includes(id);
    const nextCats = has ? bingo.categories.filter((c) => c !== id) : [...bingo.categories, id];
    patchBingo({ categories: nextCats, preset: "custom" });
  };

  return (
    <div className="space-y-4 rounded-xl border border-orange-500/20 bg-orange-500/5 p-4">
      <p className="text-sm font-semibold text-orange-300">🎱 Paramètres Bingo</p>

      <div className="grid grid-cols-2 gap-2">
        {(["easy", "normal", "expert", "custom"] as const).map((preset) => (
          <button
            key={preset}
            type="button"
            className={`cw-btn text-sm py-2 ${bingo.preset === preset ? "cw-btn-primary" : "cw-btn-secondary"}`}
            onClick={() => applyPreset(preset)}
          >
            {preset === "easy" && "🟢 Facile"}
            {preset === "normal" && "🟠 Normal"}
            {preset === "expert" && "🔴 Expert"}
            {preset === "custom" && "⚙️ Perso"}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="text-sm">Taille de grille</span>
        <select
          className="cw-input mt-1"
          value={bingo.gridSize}
          onChange={(e) => patchBingo({ gridSize: Number(e.target.value) as BingoGridSize, preset: "custom" })}
        >
          {BINGO_GRID_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} × {size}
            </option>
          ))}
        </select>
      </label>

      <fieldset>
        <legend className="text-sm mb-2">Timer de réponse</legend>
        <div className="flex flex-wrap gap-2">
          {TIMER_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              className={`cw-btn text-sm py-2 px-3 ${bingo.timerSeconds === opt.value ? "cw-btn-primary" : "cw-btn-secondary"}`}
              onClick={() => patchBingo({ timerSeconds: opt.value, preset: "custom" })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm">Victoire</legend>
        {([
          ["horizontal", "Lignes horizontales"],
          ["vertical", "Lignes verticales"],
          ["diagonal", "Diagonales"],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={bingo.winConditions[key]}
              onChange={(e) =>
                patchBingo({
                  winConditions: { ...bingo.winConditions, [key]: e.target.checked },
                  preset: "custom",
                })
              }
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={bingo.exactYearPower}
          onChange={(e) => patchBingo({ exactYearPower: e.target.checked, preset: "custom" })}
        />
        <span>Pouvoir année exacte (retirer une case à un adversaire)</span>
      </label>

      <fieldset>
        <legend className="text-sm mb-2">Catégories</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {categories.map((cat) => {
            const selected = bingo.categories.includes(cat.id);
            const issue = validation?.issues.find((i) => i.categoryId === cat.id);
            return (
              <label
                key={cat.id}
                className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${
                  selected ? "border-violet-400/40 bg-violet-500/10" : "border-white/10"
                }`}
              >
                <input type="checkbox" checked={selected} onChange={() => toggleCategory(cat.id)} className="mt-1" />
                <span>
                  <span className="font-medium">
                    {cat.icon} {cat.name}
                  </span>
                  <span className="block text-xs text-violet-300/70">{cat.description}</span>
                  {issue && (
                    <span className={`block text-xs mt-1 ${issue.severity === "block" ? "text-red-400" : "text-yellow-400"}`}>
                      {issue.message}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {validation && !validation.ok && (
        <p className="text-sm text-red-400" role="alert">
          Cette configuration ne peut pas être lancée tant que les catégories bloquantes ne sont pas corrigées.
        </p>
      )}
    </div>
  );
}
